import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@apollo/client';
import { PUBLIC_FOOD_ITEMS_QUERY } from '../graphql';

const FOOD_CATEGORY_LABELS = {
  tiffin:    '🍱 Tiffin',
  breakfast: '🌅 Breakfast',
  sweets:    '🍬 Sweets',
  snacks:    '🥨 Snacks',
  bakes:     '🎂 Bakes',
  pickles:   '🫙 Pickles',
  biryani:   '🍛 Biryani',
  curry:     '🫕 Curry',
  thali:     '🍽️ Thali',
  beverages: '🥤 Drinks',
};

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function fmtDist(km) {
  if (km < 1) return `${Math.round(km * 1000)} m away`;
  return `${km.toFixed(1)} km away`;
}

export default function FoodPage({ onAuthRequired }) {
  const { data, loading } = useQuery(PUBLIC_FOOD_ITEMS_QUERY);
  const [userPos, setUserPos] = useState(null);
  const [locating, setLocating] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const detect = useCallback(() => {
    if (!navigator.geolocation) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => { setUserPos({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      () => setLocating(false)
    );
  }, []);

  useEffect(() => { detect(); }, [detect]);

  const allItems = data?.publicFoodItems || [];

  const items = allItems
    .filter(item => filter === 'all' || item.foodCategory === filter)
    .filter(item => !search || item.name.toLowerCase().includes(search.toLowerCase()) || item.creator?.name?.toLowerCase().includes(search.toLowerCase()) || item.pickupAddress.toLowerCase().includes(search.toLowerCase()))
    .map(item => {
      const dist = (userPos && item.pickupLat && item.pickupLng)
        ? haversineKm(userPos.lat, userPos.lng, item.pickupLat, item.pickupLng)
        : null;
      return { ...item, dist };
    })
    .sort((a, b) => {
      if (a.dist !== null && b.dist !== null) return a.dist - b.dist;
      if (a.dist !== null) return -1;
      if (b.dist !== null) return 1;
      return 0;
    });

  const categories = ['all', ...Object.keys(FOOD_CATEGORY_LABELS)];

  return (
    <div style={{ minHeight: '100vh', background: '#FAFAF8' }}>
      {/* Hero banner */}
      <div style={{ background: 'linear-gradient(135deg, #C05621 0%, #9A3412 100%)', color: '#fff', padding: '80px 20px 40px', textAlign: 'center' }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🍽️</div>
        <h1 style={{ fontSize: 'clamp(24px, 5vw, 40px)', fontWeight: 900, marginBottom: 8, letterSpacing: '-0.02em' }}>
          Agora Food
        </h1>
        <p style={{ fontSize: 16, opacity: 0.88, marginBottom: 24, maxWidth: 480, margin: '0 auto 24px' }}>
          Home-cooked meals from <strong>grahani</strong> in your neighbourhood — fresh, local, with love.
        </p>
        <div style={{ maxWidth: 520, margin: '0 auto', display: 'flex', gap: 10 }}>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search biryani, tiffin, sweets…"
            style={{ flex: 1, padding: '12px 16px', borderRadius: 10, border: 'none', fontSize: 15, fontFamily: 'inherit', outline: 'none', color: '#1C1917' }}
          />
          {!userPos ? (
            <button onClick={detect} disabled={locating}
              style={{ padding: '12px 16px', borderRadius: 10, border: 'none', background: 'rgba(255,255,255,0.2)', color: '#fff', cursor: locating ? 'wait' : 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
              {locating ? '📍…' : '📍 Near me'}
            </button>
          ) : (
            <div style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
              ✓ Location set
            </div>
          )}
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '24px 16px' }}>
        {/* Category chips */}
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 8, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', marginBottom: 20 }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setFilter(cat)}
              style={{ padding: '8px 16px', borderRadius: 20, border: `2px solid ${filter === cat ? '#C05621' : '#E8E4DC'}`, background: filter === cat ? '#C05621' : '#fff', color: filter === cat ? '#fff' : '#57534E', cursor: 'pointer', fontWeight: 700, fontSize: 13, fontFamily: 'inherit', whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s' }}>
              {cat === 'all' ? '🍽️ All' : FOOD_CATEGORY_LABELS[cat]}
            </button>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 60, color: '#78716C' }}>Loading home kitchens…</div>
        )}

        {!loading && items.length === 0 && (
          <div style={{ textAlign: 'center', padding: 60 }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🥘</div>
            <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>No food listings yet</div>
            <div style={{ color: '#78716C', fontSize: 15 }}>Be the first home cook in your community — list your food via any group shop.</div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {items.map(item => (
            <FoodCard key={item.id} item={item} />
          ))}
        </div>

        {items.length > 0 && (
          <p style={{ textAlign: 'center', marginTop: 32, fontSize: 13, color: '#A8A29E' }}>
            {items.length} home-kitchen listing{items.length !== 1 ? 's' : ''}{userPos ? ' · sorted by distance' : ''}
          </p>
        )}
      </div>
    </div>
  );
}

function FoodCard({ item }) {
  const [showOrder, setShowOrder] = useState(false);

  return (
    <>
      <div
        style={{ background: '#fff', borderRadius: 14, border: '1px solid #E8E4DC', overflow: 'hidden', boxShadow: '0 1px 6px rgba(0,0,0,0.06)', transition: 'box-shadow 0.15s', cursor: 'pointer' }}
        onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 18px rgba(0,0,0,0.12)'}
        onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'}
      >
        <div style={{ width: '100%', height: 180, background: 'linear-gradient(135deg, #FEF3EC, #F3E8DC)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
          {item.imageUrl
            ? <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <span style={{ fontSize: 64 }}>{item.imageEmoji}</span>}
          <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(255,255,255,0.95)', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#C05621' }}>
            {FOOD_CATEGORY_LABELS[item.foodCategory] || item.foodCategory}
          </div>
          {item.dist !== null && (
            <div style={{ position: 'absolute', top: 10, right: 10, background: 'rgba(0,0,0,0.72)', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700, color: '#fff' }}>
              📍 {fmtDist(item.dist)}
            </div>
          )}
        </div>

        <div style={{ padding: '14px 16px' }}>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4, lineHeight: 1.3 }}>{item.name}</div>
          {item.description && (
            <div style={{ fontSize: 13, color: '#57534E', marginBottom: 8, lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {item.description}
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: item.creator?.avatarColor || '#C05621', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#fff', flexShrink: 0 }}>
              {(item.creator?.name || '?')[0].toUpperCase()}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{item.creator?.name || 'Home Chef'}</div>
              {item.creator?.sellerRatingAvg > 0 && (
                <div style={{ fontSize: 11, color: '#78716C' }}>{'★'.repeat(Math.round(item.creator.sellerRatingAvg))} ({item.creator.sellerRatingCount})</div>
              )}
            </div>
          </div>

          <div style={{ fontSize: 12, color: '#78716C', marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 4 }}>
            <span style={{ flexShrink: 0 }}>🏠</span>
            <span>Pickup: {item.pickupAddress}</span>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ fontWeight: 900, fontSize: 18, color: '#C05621' }}>
              {item.price === 0 ? 'Free' : `₹${item.price}`}
            </div>
            <button
              onClick={() => setShowOrder(true)}
              style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#C05621', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14, fontFamily: 'inherit' }}>
              Order
            </button>
          </div>
        </div>
      </div>

      {showOrder && <OrderModal item={item} onClose={() => setShowOrder(false)} />}
    </>
  );
}

function OrderModal({ item, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300, padding: 16 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 400, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div style={{ fontWeight: 800, fontSize: 18 }}>{item.name}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: '#78716C', padding: '0 4px', lineHeight: 1 }}>✕</button>
        </div>

        {item.imageUrl && (
          <img src={item.imageUrl} alt={item.name} style={{ width: '100%', height: 160, objectFit: 'cover', borderRadius: 10, marginBottom: 14 }} />
        )}

        <div style={{ fontSize: 14, color: '#57534E', marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div>🏠 <strong>Pickup from:</strong> {item.pickupAddress}</div>
          <div>👩‍🍳 <strong>Seller:</strong> {item.creator?.name}</div>
          <div>💰 <strong>Price:</strong> {item.price === 0 ? 'Free' : `₹${item.price}`} per serving</div>
          {item.dist !== null && <div>📍 <strong>Distance:</strong> {fmtDist(item.dist)}</div>}
        </div>

        <div style={{ padding: '12px 14px', background: '#FEF3EC', borderRadius: 10, border: '1px solid #C05621', marginBottom: 16, fontSize: 13, color: '#7C2D12' }}>
          <strong>Pickup only</strong> — contact the seller after ordering to arrange pickup time. This is a home kitchen listing.
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid #E8E4DC', background: '#fff', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit', fontSize: 14, color: '#57534E' }}>Cancel</button>
          <a href={`/group/${item.groupId}`} style={{ flex: 2, padding: '10px 0', borderRadius: 8, border: 'none', background: '#C05621', color: '#fff', cursor: 'pointer', fontWeight: 700, fontFamily: 'inherit', fontSize: 14, textAlign: 'center', textDecoration: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            Go to Group Shop →
          </a>
        </div>
      </div>
    </div>
  );
}
